import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { SmartintegService } from '../../../../services/smartinteg.service';
import { SmartintegVisualizacaoService } from '../../../../services/smartinteg-visualizacao.service';
import { ProcessoVisualizacaoResponse } from '../../../../types/smartinteg';
import { ToastService } from '../../../../services/toast.service';

@Component({
  selector: 'app-processo-monitor',
  templateUrl: './processo-monitor.component.html',
  styleUrls: ['./processo-monitor.component.css'],
  providers: [ConfirmationService]
})
export class ProcessoMonitorComponent implements OnInit {

  processos: ProcessoVisualizacaoResponse[] = [];
  loading: boolean = true;
  erro: boolean = false;
  filtroTipo: 'todos' | 'recebimento' | 'envio' = 'todos';
  termoBusca: string = '';
  executandoId: string | null = null;

  constructor(
    private visualizacaoService: SmartintegVisualizacaoService,
    private smartintegService: SmartintegService,
    private confirmationService: ConfirmationService,
    private toastService: ToastService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.carregarProcessos();
  }

  carregarProcessos(silencioso = false): void {
    if (!silencioso) {
      this.loading = true;
      this.erro = false;
    }
    this.visualizacaoService.listarProcessosVisualizacao(true)
      .subscribe({
        next: (data) => {
          this.processos = [...data];
          this.loading = false;
        },
        error: (err) => {
          this.loading = false;
          if (!silencioso) this.erro = true;
          console.error('Erro ao carregar processos monitor:', err);
        }
      });
  }

  get processosFiltrados(): ProcessoVisualizacaoResponse[] {
    return this.processos
      .filter(p => {
        if (this.filtroTipo === 'recebimento') return (p.estatisticas?.registrosRecebidos?.total ?? 0) > 0;
        if (this.filtroTipo === 'envio') return (p.estatisticas?.registrosVisualizacao?.total ?? 0) > 0;
        return true;
      })
      .filter(p => !this.termoBusca || p.nome.toLowerCase().includes(this.termoBusca.toLowerCase()));
  }

  get totalAtivos(): number {
    return this.processos.filter(p => p.ativo).length;
  }

  get totalFalhas(): number {
    return this.processos.reduce((acc, p) => {
      return acc
        + (p.estatisticas?.registrosRecebidos?.erro ?? 0)
        + (p.estatisticas?.registrosVisualizacao?.erro ?? 0);
    }, 0);
  }

  get taxaMediaSucesso(): number {
    const recebimento = this.processos.filter(p => (p.estatisticas?.registrosRecebidos?.total ?? 0) > 0);
    if (recebimento.length === 0) return 0;
    const soma = recebimento.reduce((acc, p) => acc + this.getPercentualSucesso(p), 0);
    return soma / recebimento.length;
  }

  getTotalRegistros(proc: ProcessoVisualizacaoResponse): number {
    return (proc.estatisticas?.registrosRecebidos?.total ?? 0)
      + (proc.estatisticas?.registrosVisualizacao?.total ?? 0)
      + (proc.estatisticas?.registrosPublicacao?.total ?? 0);
  }

  getTotalSucesso(proc: ProcessoVisualizacaoResponse): number {
    return (proc.estatisticas?.registrosRecebidos?.concluido ?? 0)
      + (proc.estatisticas?.registrosVisualizacao?.integrado ?? 0);
  }

  getTotalFalhas(proc: ProcessoVisualizacaoResponse): number {
    return (proc.estatisticas?.registrosRecebidos?.erro ?? 0)
      + (proc.estatisticas?.registrosVisualizacao?.erro ?? 0);
  }

  getPercentualSucesso(proc: ProcessoVisualizacaoResponse): number {
    const total = this.getTotalRegistros(proc);
    if (total === 0) return 0;
    return (this.getTotalSucesso(proc) / total) * 100;
  }

  getProgressBarColor(proc: ProcessoVisualizacaoResponse): string {
    const pct = this.getPercentualSucesso(proc);
    if (pct >= 90) return '#22c55e';
    if (pct >= 50) return '#f59e0b';
    return '#ef4444';
  }

  getTipoLabel(proc: ProcessoVisualizacaoResponse): string {
    if ((proc.estatisticas?.registrosRecebidos?.total ?? 0) > 0) return 'Recebimento';
    if ((proc.estatisticas?.registrosVisualizacao?.total ?? 0) > 0) return 'Envio';
    return 'Sem registros';
  }

  getTipoSeverity(proc: ProcessoVisualizacaoResponse): string {
    if ((proc.estatisticas?.registrosRecebidos?.total ?? 0) > 0) return 'info';
    if ((proc.estatisticas?.registrosVisualizacao?.total ?? 0) > 0) return 'warning';
    return 'secondary';
  }

  getTipoIcon(proc: ProcessoVisualizacaoResponse): string {
    if ((proc.estatisticas?.registrosRecebidos?.total ?? 0) > 0) return 'pi pi-arrow-down';
    if ((proc.estatisticas?.registrosVisualizacao?.total ?? 0) > 0) return 'pi pi-arrow-up';
    return 'pi pi-minus';
  }

  alterarStatus(proc: ProcessoVisualizacaoResponse): void {
    this.smartintegService.alterarStatusProcesso(proc.id).subscribe({
      next: () => this.carregarProcessos(true),
      error: () => {
        proc.ativo = !proc.ativo; // reverte o toggle em caso de erro
        this.toastService.erro('Erro', `Falha ao alterar status do processo '${proc.nome}'.`);
      }
    });
  }

  confirmarExecucao(proc: ProcessoVisualizacaoResponse): void {
    this.confirmationService.confirm({
      message: `Deseja executar o processo <strong>${proc.nome}</strong>?`,
      header: 'Confirmar Execução',
      icon: 'pi pi-play',
      acceptLabel: 'Executar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-success',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => this.executarProcesso(proc)
    });
  }

  executarProcesso(proc: ProcessoVisualizacaoResponse): void {
    this.executandoId = proc.id;
    this.smartintegService.executarProcesso(proc.id).subscribe({
      next: (_) => {
        this.executandoId = null;
        
        this.toastService.sucesso('Processo executado', 'Concluído com sucesso.');

        this.carregarProcessos(true);
      },
      error: () => {
        this.executandoId = null;
        this.toastService.erro('Erro', `Falha ao executar o processo '${proc.nome}'.`);
        this.carregarProcessos(true);
      }
    });
  }

  verRegistros(proc: ProcessoVisualizacaoResponse): void {
    if ((proc.estatisticas?.registrosVisualizacao?.total ?? 0) > 0) {
      this.router.navigate(['/smartinteg/monitor/processos', proc.id, 'visualizacao']);
    } else {
      this.router.navigate(['/smartinteg/monitor/processos', proc.id, 'recebidos']);
    }
  }
}
